import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import CustomButton from "src/components/CustomButton";
import { ADD_POPULAR_LOCATION_ENUM } from "src/graphql/mutations";
import { toast } from "react-toastify";
import { UPDATE_POPULAR_LOCATION_ENUM } from "src/graphql/mutations";

interface DealsModalProps {
  open: boolean;
  handleClose: () => void;
  refetchDeals: () => void;
  selectedDealEnum?: any;
}

const CreatePopularLocationEnum: React.FC<DealsModalProps> = ({
  open,
  handleClose,
  refetchDeals,
  selectedDealEnum,
}) => {
  const [AddPopularLocationEnum] = useMutation(ADD_POPULAR_LOCATION_ENUM);
  const [UpdatePopularLocationEnum] = useMutation(UPDATE_POPULAR_LOCATION_ENUM);
  const initialValues = {
    name: selectedDealEnum?.name || "",
    description: selectedDealEnum?.description || "",
    rank: selectedDealEnum?.rank || 0,
    metaTitle: selectedDealEnum?.metaTitle || "",
    metaKeywords: selectedDealEnum?.metaKeywords || "",
    focus: selectedDealEnum?.focus || "",
    metaDescription: selectedDealEnum?.metaDescription || "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    description: Yup.string().required("Description is required"),
    rank: Yup.number().nullable().typeError("Rank must be a number"),
    metaTitle: Yup.string().nullable(),
    metaKeywords: Yup.string().nullable(),
    focus: Yup.string().nullable(),
    metaDescription: Yup.string().nullable(),
  });

  const handleSubmit = async (
    values: typeof initialValues,
    { setErrors }: any
  ) => {
    try {
      const variables = {
        input: {
          name: values.name,
          description: values.description,
          rank: values.rank,
          metaTitle: values.metaTitle,
          metaKeywords: values.metaKeywords,
          focus: values.focus,
          metaDescription: values.metaDescription,
        },
      };

      let res;
      if (selectedDealEnum) {
        // Update existing deal
        res = await UpdatePopularLocationEnum({
          variables: { _id: selectedDealEnum?._id, ...variables },
        });
      } else {
        // Add new deal
        res = await AddPopularLocationEnum({ variables });
      }

      if (
        res?.data?.addPopularLocationEnum?.statusCode === 200 ||
        res?.data?.updatePopularLocationEnum?.statusCode === 200
      ) {
        toast.success(
          res?.data?.addPopularLocationEnum?.message ||
            res?.data?.updatePopularLocationEnum?.message ||
            "Operation successful"
        );
        handleClose();
        refetchDeals();
      } else {
        throw new Error(res?.errors as any);
      }
    } catch (err: any) {
      toast.error(err.message || "An error occurred during the operation.");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        {selectedDealEnum
          ? "Edit Popular Location Enum"
          : "Add Popular Location Enum"}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched }) => (
          <Form>
            <DialogContent>
              <Field
                name="name"
                as={TextField}
                margin="dense"
                label="Name"
                fullWidth
                error={!!errors.name && touched.name}
                helperText={<ErrorMessage name="name" />}
              />

              <Field
                name="description"
                as={TextField}
                margin="dense"
                label="Description"
                fullWidth
                error={!!errors.description && touched.description}
                helperText={<ErrorMessage name="description" />}
              />

              <Field
                name="rank"
                as={TextField}
                margin="dense"
                label="Rank"
                type="number"
                fullWidth
                error={!!errors.rank && touched.rank}
                helperText={<ErrorMessage name="rank" />}
              />

              <Field
                name="metaTitle"
                as={TextField}
                margin="dense"
                label="Meta Title"
                fullWidth
                error={!!errors.metaTitle && touched.metaTitle}
                helperText={<ErrorMessage name="metaTitle" />}
              />

              <Field
                name="metaKeywords"
                as={TextField}
                margin="dense"
                label="Meta Keywords"
                fullWidth
                error={!!errors.metaKeywords && touched.metaKeywords}
                helperText={<ErrorMessage name="metaKeywords" />}
              />

              <Field
                name="focus"
                as={TextField}
                margin="dense"
                label="Focus"
                fullWidth
                error={!!errors.focus && touched.focus}
                helperText={<ErrorMessage name="focus" />}
              />

              <Field
                name="metaDescription"
                as={TextField}
                margin="dense"
                label="Meta Description"
                fullWidth
                error={!!errors.metaDescription && touched.metaDescription}
                helperText={<ErrorMessage name="metaDescription" />}
              />
            </DialogContent>
            <DialogActions>
              <CustomButton variant="outlined" onClick={handleClose}>
                Cancel
              </CustomButton>
              <CustomButton type="submit" variant="contained">
                {selectedDealEnum
                  ? "Update Location Enum"
                  : "Add Location Enum"}
              </CustomButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreatePopularLocationEnum;
